<template>
  <Modal
    ref="modal"
    form
    :full-height="$isMobile"
    offset-menu
    :title="$t('modals.request_media_item_modal.title')"
    @submit="confirmRequest"
  >
    <div class="field-heading q-mb-sm">
      {{ mentionType }}
    </div>
    <template v-if="mentionSyndications.length">
      <InputSelect
        v-model="syndicationId"
        emit-value
        new-design
        option-label="label"
        option-value="id"
        :options="mentionSyndications"
      />
      <div class="field-subtext q-mt-sm">
        {{
          $t("modals.request_media_item_modal.select_syndication_description")
        }}
      </div>
    </template>
    <div v-else>{{ mention.source.name }} {{ mention.source.location }}</div>

    <template v-if="mention.program_airing">
      <div class="field-heading q-mb-sm q-mt-lg">
        {{ $t("modals.request_media_item_modal.program_heading") }}
      </div>
      <div>
        {{ mention.program_airing.name }}
      </div>
    </template>

    <div class="field-heading q-mb-sm q-mt-lg">
      {{ $t("modals.request_media_item_modal.starting_time_heading") }}
    </div>
    <div>
      {{
        formatIntlDate(mentionStartTime, {
          dateStyle: "short",
          timeStyle: "medium",
          timeZone: selectedMention.source.time_zone,
        })
      }}
      ({{ selectedMention.source.time_zone }})
    </div>

    <div class="field-heading q-mb-sm q-mt-lg">
      {{ $t("modals.request_media_item_modal.additional_recipients_heading") }}
    </div>
    <DistributedEmailInput
      v-model="recipients"
      new-design
    />

    <InputText
      v-model="contextDuration"
      class="q-mt-lg"
      :height="80"
      :label="$t('modals.request_media_item_modal.context_duration_label')"
      new-design
      type="textarea"
      :validations="['notBlank']"
    />
    <div class="field-subtext q-mt-sm">
      {{ contextDurationPlaceholder }}
    </div>

    <div
      v-if="canAcceptTerms"
      class="q-mt-lg"
    >
      <Checkbox
        v-model="acceptedTerms"
        align-top
        new-design
      >
        <div class="q-pl-md">
          <div class="text-help-text">
            {{
              $t("modals.request_media_item_modal.help_text", {
                organisationName: organisation.name,
                platformName: appConfig.platformName,
              })
            }}
          </div>
          <div
            v-if="submitted && !acceptedTerms"
            class="field-error q-mt-sm"
          >
            {{ $t("modals.request_media_item_modal.required_error") }}
          </div>
        </div>
      </Checkbox>
    </div>

    <template #buttons>
      <BaseButton
        color="white"
        :label="$t('global.cancel')"
        new-design
        @click.prevent="$modals.close('RequestMediaItemModal')"
      />
      <BaseButton
        color="primary"
        :label="$t('modals.request_media_item_modal.confirm_button')"
        new-design
        type="submit"
      />
    </template>
  </Modal>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseButton, InputSelect, InputText } from "shared/components/base";
import DistributedEmailInput from "shared/components/core/forms/DistributedEmailInput.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import useTeamPicker from "shared/composables/useTeamPicker";
import { formatIntlDate, toMilliseconds } from "shared/helpers/date";
import { MediaRequest, OrganisationContract } from "shared/resources";
import { useGlobalStore } from "shared/stores/global";
import { useOrganisationFeaturesStore } from "shared/stores/organisationFeatures";
import { useUserStore } from "shared/stores/user";

export default {
  name: "RequestMediaItemModal",
  components: {
    DistributedEmailInput,
    BaseButton,
    InputSelect,
    InputText,
    Modal,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    origin: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    mediaRequest: {
      type: Object,
      default: null,
    },
  },
  setup() {
    const { refreshUsedCredits } = useOrganisationFeaturesStore();

    const userStore = useUserStore();
    const { adminUserEnabled, organisation } = storeToRefs(userStore);

    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    const { selectedTeamPreference } = useTeamPicker();

    return {
      refreshUsedCredits,
      adminUserEnabled,
      organisation,
      appConfig,
      selectedTeamPreference,
    };
  },
  data() {
    return {
      acceptedTerms: false,
      submitted: false,
      contextDuration: "",
      recipients: [],
      syndicationId: 0,
      syndication: null,
      organisationContract: new OrganisationContract(),
    };
  },
  computed: {
    contextDurationPlaceholder() {
      return this.$t(
        "modals.request_media_item_modal.context_duration_label_description",
        { platformName: this.appConfig.platformName }
      );
    },
    reachedContractAllowance() {
      return this.$features.reachedContractAllowance("media_request");
    },
    canAcceptTerms() {
      return !this.$isAdminMode && !this.adminUserEnabled;
    },
    syndicationsArePresent() {
      return this.syndication && Object.keys(this.syndication).length;
    },
    mentionSyndications() {
      if (this.mention.syndications && this.mention.syndications.length) {
        const syndications = this.mention.syndications.map((syndication) => ({
          label: syndication.source_name,
          value: syndication,
          id: syndication.id,
        }));

        syndications.unshift({
          label: `${this.mention.source.name} ${this.mention.source.location}`,
          value: this.mention,
          id: this.mention.id,
        });

        return syndications;
      }

      return [];
    },
    mentionType() {
      return this.mention.type === "radio_clip"
        ? this.$t("modals.request_media_item_modal.mention_type_station")
        : this.$t("modals.request_media_item_modal.mention_type_channel");
    },
    mentionStartTime() {
      return this.syndicationsArePresent
        ? this.syndication.start_time
        : this.mention.start_time;
    },
    selectedMention() {
      return this.syndicationsArePresent ? this.syndication : this.mention;
    },
    sourceId() {
      if (this.syndicationsArePresent) {
        return this.syndication.source
          ? this.syndication.source.id
          : this.syndication.source_id;
      }

      return this.mention.source.id;
    },
    startTime() {
      return this.mediaRequest
        ? this.mediaRequest.startTime
        : toMilliseconds(this.selectedMention.start_time);
    },
    endTime() {
      return this.mediaRequest
        ? this.mediaRequest.endTime
        : toMilliseconds(this.selectedMention.end_time);
    },
  },
  watch: {
    syndicationId(value) {
      const syndication = this.mentionSyndications.find(
        (mentionSyndication) => mentionSyndication.id === value
      );

      if (syndication) {
        this.syndication = syndication.value;
      }
    },
  },
  async mounted() {
    this.syndication = this.mentionSyndications.length ? this.mention : null;

    if (this.syndication) {
      this.syndicationId = this.syndication.id;
    }

    await this.refreshUsedCredits({
      organisationId: this.organisation.id,
      feature: "media_request",
    });

    if (this.$isAdminMode) {
      this.organisationContract = await this.loadOrganisationContract();
    }

    if (this.mediaRequest) {
      this.prefillMediaRequest();
    }
  },
  methods: {
    formatIntlDate,
    prefillMediaRequest() {
      if (this.mediaRequest?.title && this.mediaRequest?.summary) {
        this.contextDuration = `${this.mediaRequest.title}\n${this.mediaRequest.summary}`;
      }

      const selectedSyndication = this.mentionSyndications.find(
        (source) =>
          source.value.source.id === Number(this.mediaRequest.sourceId)
      );

      if (selectedSyndication) {
        this.syndication = selectedSyndication.value;
      }

      this.recipients = this.mediaRequest.recipients || [];
    },
    loadOrganisationContract() {
      return OrganisationContract.where({
        organisationId: this.organisationId,
        contractType: 1,
      })
        .order({ contractStart: "desc" })
        .first();
    },
    confirmRequest() {
      this.submitted = true;

      if (this.canAcceptTerms && !this.acceptedTerms) {
        return;
      }

      if (this.reachedContractAllowance) {
        this.$modals.open("RequestMediaTranscriptItemConfirmModal", {
          props: {
            type: "media_request",
          },
          events: {
            confirm: () => {
              this.send();
            },
          },
        });
      } else {
        this.send();
      }
    },
    async send() {
      this.$q.loading.show({ delay: 0 });

      try {
        const mediaRequest = new MediaRequest({
          ...this.origin,
          sourceId: this.sourceId,
          sourceType: this.mention.source.type,
          billable: true,
          startTime: this.startTime,
          endTime: this.endTime,
          notes: this.contextDuration,
          timeZone: this.selectedMention.source.timezone,
          recipients: this.recipients,
          notifyOnCompletion: true,
          ...(this.$features.has("has_team_workspaces")
            ? { organisation_team_id: this.selectedTeamPreference }
            : {}),
        });

        await mediaRequest.saveResource();

        this.$track("Requested Media Item", {
          mentionId: this.mention.id,
          mentionType: this.mention.type,
          sourceId: this.sourceId,
        });

        this.$q.notify({
          message: this.$t("modals.request_media_item_modal.notify_message"),
          html: true,
          position: "center",
        });
      } finally {
        this.$q.loading.hide();
        this.closeModal();
      }
    },
    closeModal() {
      this.$refs.modal.close();
    },
  },
};
</script>
